import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from "classnames";
import { format } from "date-fns";
import InfiniteScroll from "react-infinite-scroll-component";
import { getLastMessage } from "../utils";
import ImageModal from "../components/ImageModal"

const convertBase64 = (file) => {
    return new Promise((resolve, reject) => {
        const fileReader = new FileReader();
        fileReader.readAsDataURL(file)
        fileReader.onload = () => {
            resolve(fileReader.result);
        }
        fileReader.onerror = (error) => {
            reject(error);
        }
    })
}


const ChatWindow = ({
    title,
    groupedMessages,
    isPending,
    onBack,
    toggleChat,
    onMessage,
    isTyping,
    getNext,
    canAttachFile = false
}) => {
    const [input, setInput] = useState('');
    const [selectedImage, setSelectedImage] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const canSendMessage = !isPending && (input.trim().length > 0 || selectedFile !== null);

    const messagesRef = useRef(null);
    const typingRef = useRef(null);

    const dataLength = groupedMessages.reduce((acc, cur) => acc + cur[1].length, 0);

    const [chatWidth, setChatWidth] = useState(350); // Начальная ширина
    const [isResizing, setIsResizing] = useState(false);

    const handleMouseDown = (event) => {
        setIsResizing(true);
        event.preventDefault();
    };

    const handleMouseMove = (event) => {
        if (isResizing) {
            const newWidth = Math.max(300, event.clientX - 100); // Минимальная ширина - 300px
            setChatWidth(newWidth);
        }
    };

    const handleMouseUp = () => {
        setIsResizing(false);
    };

    useEffect(() => {
        if (isResizing) {
            document.addEventListener("mousemove", handleMouseMove);
            document.addEventListener("mouseup", handleMouseUp);
        } else {
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
        }
    }, [isResizing]);



    const handleFileRead = async (event) => {
        const file = event.target.files[0];
        if (!file) return;
        setSelectedFile(file);
    };

    const handleSendFile = async () => {
        if (!selectedFile) return;

        const base64 = await convertBase64(selectedFile);
        onMessage?.(input, base64);

        setSelectedFile(null);
        setInput("");
        scrollToTyping();
    };

    const scrollToTyping = useCallback(() => {
        setTimeout(() => {
            typingRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
        }, 100)
    }, [])

    const scrollToLatestMessage = useCallback(() => {
        setTimeout(() => {
            const children = messagesRef?.current?.querySelectorAll('.message');
            const latest = children?.[children.length - 1];
            latest?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100)
    }, [])


    const handleSend = async (event) => {
        event.preventDefault();
        if (!canSendMessage) return;

        let base64 = '';
        if (selectedFile) {
            base64 = await convertBase64(selectedFile);
        }

        onMessage?.(input, base64);
        setInput("");
        setSelectedFile(null);
        scrollToTyping();
    };

    const latestTimestamp = useMemo(
        () => getLastMessage(groupedMessages)?.timestamp,
        [groupedMessages]
    );

    useEffect(() => {
        if (isTyping) scrollToTyping();
    }, [isTyping]);

    useEffect(() => {
        scrollToLatestMessage();
    }, [latestTimestamp, scrollToLatestMessage]);

    const handleImageClick = (src) => {
        setSelectedImage(src);
    };

    const closeModal = () => {
        setSelectedImage(null);
    };

    return (
        <div className="chat-window">
            {/* Модалка для фото */}
            <ImageModal src={selectedImage} onClose={closeModal} />

            <div className="chat-app__head">
                <div className="chat-app__wrap">
                    <button onClick={onBack} className="back-button">
                        <svg width="17" height="14" viewBox="0 0 17 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M-0.165039 7.1543C-0.165039 6.93457 -0.0683594 6.73242 0.107422 6.56543L5.92578 0.755859C6.11914 0.5625 6.30371 0.492188 6.51465 0.492188C6.94531 0.492188 7.28809 0.808594 7.28809 1.24805C7.28809 1.45898 7.20898 1.66113 7.06836 1.80176L5.09961 3.80566L2.14648 6.50391L4.26465 6.37207H15.3828C15.8398 6.37207 16.1562 6.69727 16.1562 7.1543C16.1562 7.61133 15.8398 7.93652 15.3828 7.93652H4.26465L2.1377 7.80469L5.09961 10.5029L7.06836 12.5068C7.20898 12.6387 7.28809 12.8496 7.28809 13.0605C7.28809 13.5 6.94531 13.8164 6.51465 13.8164C6.30371 13.8164 6.11914 13.7373 5.94336 13.5703L0.107422 7.74316C-0.0683594 7.57617 -0.165039 7.37402 -0.165039 7.1543Z"
                                fill="#707991" />
                        </svg>
                    </button>
                    <h2>{title}</h2>
                </div>
                <button onClick={toggleChat}>
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M3.75684 14.3145C3.51758 14.082 3.52441 13.6855 3.75684 13.4531L8.13184 9.07129L3.75684 4.69629C3.52441 4.46387 3.52441 4.07422 3.75684 3.83496C3.98926 3.58887 4.39258 3.5957 4.625 3.83496L9 8.20996L13.375 3.83496C13.6074 3.5957 14.0039 3.5957 14.2432 3.83496C14.4824 4.06738 14.4756 4.46387 14.2432 4.69629L9.86816 9.07129L14.2432 13.4531C14.4756 13.6855 14.4756 14.0752 14.2432 14.3145C14.0107 14.5537 13.6074 14.5469 13.375 14.3145L9 9.93945L4.625 14.3145C4.39258 14.5469 3.99609 14.5469 3.75684 14.3145Z"
                            fill="#707991" />
                    </svg>
                </button>
            </div>
            <div
                ref={messagesRef}
                id="scrollableDiv"
                className={classNames("chat-messages flex flex-col-reverse overflow-hidden overflow-y-scroll", isPending && "flex items-center justify-center")}>
                {isPending && (
                    <div role="status">
                        <svg aria-hidden="true"
                            className="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
                            viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                fill="currentColor" />
                            <path
                                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                fill="currentFill" />
                        </svg>
                        <span className="sr-only">Loading...</span>
                    </div>
                )}

                {!isPending && groupedMessages.length > 0 && (
                    <InfiniteScroll
                        next={getNext ?? (() => {
                        })}
                        inverse={true}
                        hasMore={true}
                        dataLength={dataLength}
                        loader={null}
                        scrollableTarget="scrollableDiv"
                        style={{
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                    >
                        {groupedMessages.map(([date, messages]) => (
                            <div key={date} className="flex flex-col">
                                <div className="date-label"><span>{date}</span></div>

                                {messages.map((message) => (
                                    message !== undefined ?
                                        <div key={`${message.id}-${message.timestamp}`}
                                            className={classNames("message", message.sender)}>
                                            {message.photo && (
                                                <img
                                                    src={message.photo}
                                                    alt="Прикрепленное изображение"
                                                    className="message-photo"
                                                    onClick={() => {
                                                        console.log("Клик по изображению:", message.photo);
                                                        setSelectedImage(message.photo);
                                                    }}
                                                    style={{ cursor: "pointer", maxWidth: "200px", borderRadius: "8px" }}
                                                />
                                            )}
                                            <div className="message-text-container">
                                                <p>{message.message}</p>
                                                {message.timestamp !== undefined &&
                                                    <span className="message-time">
                                                        {format(message.timestamp, 'HH:mm')}
                                                    </span>
                                                }
                                            </div>
                                        </div> : null
                                ))}
                            </div>
                        ))}

                        {isTyping && (
                            <div ref={typingRef} className="typing">
                                <div className="typing-bubble">
                                    <div className="typing-bubble-dot"></div>
                                    <div className="typing-bubble-dot"></div>
                                    <div className="typing-bubble-dot"></div>
                                </div>
                            </div>
                        )}
                    </InfiniteScroll>
                )}
            </div>
            <form onSubmit={handleSend} className="chat-input">
                {canAttachFile && (
                    <label className="input-file">
                        <input type="file" accept="image/*" onChange={handleFileRead} />
                        <span className="file-button">📎</span>
                    </label>
                )}

                {/* Превью загруженного фото с кнопкой удаления */}
                {selectedFile && (
                    <div className="relative w-12 h-12 flex items-center justify-center">
                        <img
                            src={URL.createObjectURL(selectedFile)}
                            alt="Выбранное фото"
                            className="w-full h-full object-cover rounded-md"
                        />
                        <button
                            className="absolute -top-2 -right-2 bg-red-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center shadow-md"
                            onClick={() => setSelectedFile(null)}
                        >
                            ❌
                        </button>
                    </div>
                )}



                <input
                    type="text"
                    disabled={isPending}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Введите сообщение..."
                />

                <button type="submit" disabled={!canSendMessage}>
                    <svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M2.39973 18.3131C1.03102 19.0033 -0.480849 17.6575 0.146869 16.3086L3.22411 10.6059L8.02496 9.57493L3.22411 8.39413L0.14687 2.69286C-0.482422 1.34253 1.03102 -0.00328871 2.39974 0.686877L17.133 8.12716C17.3948 8.25938 17.6138 8.45712 17.7665 8.69913C17.9193 8.94114 18 9.21828 18 9.50075C18 9.78322 17.9193 10.0603 17.7665 10.3024C17.6138 10.5444 17.3948 10.7421 17.133 10.8743L2.39973 18.3131Z"
                            fill="#9B9BAA" />
                    </svg>
                </button>
            </form>

        </div>
    );
}

export default ChatWindow;
